import request from '@/utils/api/request';
import router from '@/router';
import get from 'lodash.get';
import factory from '@/utils/api/factory';
import TENDER_OFFER_VERSION_STATUSSES from '@/utils/constants/tender-offer-version-statuses';

export const state = () => ({
  incomingTenderInfo: {},
  incomingTenderVersions: [],
  incomingTenderVersionsInfo: {},
  selectedVersionId: null,
  incomingTenderListsInfo: {},
  isSaving: false,
  exportXlsxLoading: null,
  exportXlsxErrorText: null,
  // Используется для :key для тригера переотрисовки
  // компонента загрузчика эксельки
  exportErrorUpdateTrigger: false,
  // Флажок меняется при возникновении каких-либо ошибок или
  // если нужно актуализировать данные для
  // таблицы и отката ошибочных изменений
  refreshIncomingTableTrigger: false,
  showChangesFromCustomer: false,
  showOldAndArchivedOffers: true,
});

export const mutations = {
  changeIncomingTender(state, { key, value }) {
    state[key] = value;
  },
  clearIncomingTenderInfo(state) {
    state.incomingTenderInfo = {};
    state.incomingTenderVersions = [];
    state.selectedVersion = null;
    state.incomingTenderListsInfo = {};
    state.exportXlsxErrorText = null;
    state.exportXlsxLoading = null;
  },
  clearIncomingTenderListsInfo(state) {
    state.incomingTenderListsInfo = {};
  },
  changeExportXlsxInfo(state, { errorText }) {
    state.exportXlsxErrorText = errorText;
    state.exportErrorUpdateTrigger = !state.exportErrorUpdateTrigger;
  },
  changeExportXlsxError(state, { errorText }) {
    state.exportXlsxErrorText = errorText;
  },
  changeExportXlsxLoading(state, status) {
    state.exportXlsxLoading = status;
  },
  // Метод для локального изменения налогов при успешности их обновления,
  // чтобы не триггерить лишние запросы и не нагружать сеть
  updateVersionsTax(state, { tax, offerVersionId, action }) {
    const version = state.incomingTenderVersions?.find((version) => version.id === offerVersionId);
    if (action === 'add') {
      version.taxes.push(tax);
    } else if (action === 'delete') {
      version.taxes = version.taxes.filter((currentTax) => currentTax.id !== tax.id);
    } else if (action === 'update') {
      version.taxes = version.taxes.filter((currentTax) => currentTax.id !== tax.id);
      version.taxes.push(tax);
    }
  },
  addNewBreakdownRow(state, { rowId, createdBreakdownRow }) {
    const updatedRow = state.incomingTenderListsInfo.results.find((row) => +row.id === +rowId);
    updatedRow.breakdown_rows = [...updatedRow.breakdown_rows, createdBreakdownRow];
  },
  removeBreakdownRow(state, { rowId, breakdownRowIndex }) {
    const updatedRow = state.incomingTenderListsInfo.results.find((row) => +row.id === +rowId);
    updatedRow.breakdown_rows.splice(breakdownRowIndex, 1);
  },
  removeBreakdown(state, { rowId }) {
    const updatedRow = state.incomingTenderListsInfo.results.find((row) => +row.id === +rowId);
    updatedRow.breakdown_rows = [];
  },
  updateChangedRow(state, { rowId, updatedRow }) {
    const updatedRowIndex = state.incomingTenderListsInfo.results?.findIndex(
      (row) => +row.id === +rowId
    );
    if (~updatedRowIndex) {
      state.incomingTenderListsInfo.results[updatedRowIndex] = updatedRow;
    }
  },
  refreshIncomingTenderListsInfo(state) {
    state.incomingTenderListsInfo = { ...state.incomingTenderListsInfo };
  },
  incomingTableRefresher(state) {
    state.refreshIncomingTableTrigger = !state.refreshIncomingTableTrigger;
  },
  setChangesFromCustomer(state, value) {
    state.showChangesFromCustomer = value;
  },
  updateListSumsValue(state, updatedSums) {
    state.incomingTenderListsInfo.list_sums = updatedSums;
  },
  saving: (state, isSaving = true) => (state.isSaving = isSaving),
};

export const actions = {
  async declineOffer({ state, dispatch }) {
    try {
      const offerId = state.incomingTenderInfo?.id;
      await request.post(`/incoming_tenders/offers/${offerId}/decline/`);
      dispatch('loadIncomingTenderInfo', offerId);
    } catch (e) {
      console.error(e);
    }
  },
  async loadIncomingTenderInfo({ state, commit, dispatch }, id) {
    try {
      const { data } = await request.get(`/incoming_tenders/offers/${id}/`);
      commit('changeIncomingTender', {
        key: 'incomingTenderInfo',
        value: data,
      });

      if (!state.incomingTenderVersions.length)
        commit('changeIncomingTender', {
          key: 'incomingTenderVersions',
          value: data.versions,
        });

      if (data?.status === 'invited') {
        await request.post(`/incoming_tenders/offers/${data.id}/read/`);
        dispatch('loadIncomingTenders');
      }
    } catch (e) {
      console.error(e);
    }
  },
  // Информация о всех позиция определенной версии листа
  async loadIncomingTenderListsInfo({ commit }, { listId, offerVersionId }) {
    try {
      commit('changeIncomingTender', {
        key: 'incomingTenderListsInfo',
        value: [],
      });
      const { data } = await request.get(`/incoming_tenders/offers/rows/`, {
        params: {
          tender_offer_version_id: offerVersionId,
          list_id: listId,
        },
      });
      commit('changeIncomingTender', {
        key: 'incomingTenderListsInfo',
        value: data,
      });
    } catch (e) {
      console.error(e);
    }
    commit('refreshIncomingTenderListsInfo');
  },
  async loadIncomingTenderVersions(
    { commit, dispatch, state },
    { incomingTenderId, updateSelectedVersion = false, versionId = null }
  ) {
    try {
      const { data } = await request.get(`/incoming_tenders/offers/${incomingTenderId}/summary/`);
      if ((updateSelectedVersion || !state.selectedVersionId) && data.versions?.length) {
        if (versionId && data.versions.some((version) => version.id === versionId)) {
          dispatch('changeSelectedVersion', versionId);
        } else {
          dispatch('changeSelectedVersion', data.versions[0].id);
        }
      }
      commit('changeIncomingTender', {
        key: 'incomingTenderVersions',
        value: data.versions,
      });

      commit('changeIncomingTender', {
        key: 'incomingTenderVersionsInfo',
        value: data,
      });
    } catch (e) {
      console.error(e);
    }
  },
  changeSelectedVersion({ commit, dispatch }, versionId) {
    commit('changeIncomingTender', { key: 'selectedVersionId', value: versionId });
    const listId = router.currentRoute.params.incomingTenderListId;
    if (listId && versionId) {
      dispatch('loadIncomingTenderListsInfo', {
        listId,
        offerVersionId: versionId,
      });
    }
  },
  async createIncomingTenderVersion({ dispatch }, id) {
    try {
      await request.post('/incoming_tenders/offers/versions/', {
        tender_offer_id: id,
      });
      await dispatch('loadIncomingTenderVersions', {
        incomingTenderId: id,
        updateSelectedVersion: true,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async createIncomingTenderVersionFromOffer({ state, dispatch }, id) {
    try {
      await request.post('/incoming_tenders/offers/versions/', {
        tender_offer_id: state.incomingTenderInfo.id,
        based_on_version_id: id,
      });
      await dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
      dispatch('loadIncomingTenderInfo', state.incomingTenderInfo.id);
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async deleteIncomingTenderVersion({ dispatch, state }, id) {
    try {
      const incomingTenderId = state.incomingTenderInfo.id;
      await request.delete(`/incoming_tenders/offers/versions/${id}/`);
      await dispatch('loadIncomingTenderVersions', { incomingTenderId });
      if (id === state.selectedVersionId) {
        const atLeastOneVersion = state.incomingTenderVersions?.length;
        if (atLeastOneVersion) {
          dispatch('changeSelectedVersion', state.incomingTenderVersions[0].id);
        } else {
          dispatch('changeSelectedVersion', null);
        }
      }
    } catch (e) {
      console.error(e);
    }
  },
  async addOfferVersionTax({ state, commit, dispatch }, { taxId, percent, offerVersionId }) {
    try {
      const addedTax = await request.post('/incoming_tenders/offers/taxes/', {
        tender_tax_id: taxId,
        percent_total: percent,
        tender_offer_version_id: offerVersionId,
      });
      commit('updateVersionsTax', {
        tax: addedTax.data,
        offerVersionId,
        action: 'add',
      });
      dispatch('modal/closeModal', {}, { root: true });
      dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async removeOfferVersionTax({ state, commit, dispatch }, { taxId, offerVersionId }) {
    try {
      await request.delete(`/incoming_tenders/offers/taxes/${taxId}/`);
      commit('updateVersionsTax', {
        tax: { id: taxId },
        offerVersionId,
        action: 'delete',
      });
      dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async updateOfferVersionTax(
    { state, commit, dispatch },
    { offerTaxId, taxId, percent, offerVersionId }
  ) {
    try {
      const updatedTax = await request.put(`/incoming_tenders/offers/taxes/${offerTaxId}/`, {
        tender_tax_id: taxId,
        percent_total: percent,
        tender_offer_version_id: offerVersionId,
      });
      commit('updateVersionsTax', {
        tax: updatedTax.data,
        offerVersionId,
        action: 'update',
      });
      dispatch('modal/closeModal', {}, { root: true });
      dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
  },
  async editIncomingTenderCellValue({ commit }, { rowId, key, value }) {
    commit('saving');
    try {
      const { data } = await request.patch(`/incoming_tenders/offers/rows/${rowId}/`, {
        [key]: value,
      });
      commit('updateChangedRow', { rowId, updatedRow: data });
      commit('updateListSumsValue', data.list_sums);
    } catch (e) {
      commit('incomingTableRefresher');
      console.error(e);
    }
    commit('refreshIncomingTenderListsInfo');
    commit('saving', false);
  },
  async editIncomingTenderBreakdownCellValue({ commit }, { parentRowId, rowId, key, value }) {
    try {
      const { data } = await request.patch(
        `/incoming_tenders/offers/rows/${parentRowId}/breakdown_rows/${rowId}/`,
        {
          [key]: value,
        }
      );
      commit('updateChangedRow', {
        rowId: parentRowId,
        updatedRow: data.tender_offer_row,
      });
      commit('updateListSumsValue', data.tender_offer_row.list_sums);
    } catch (e) {
      commit('incomingTableRefresher');
      console.error(e); // eslint-disable-line
    }
    commit('refreshIncomingTenderListsInfo');
  },
  async submitOfferVersion({ dispatch }, { incomingTenderId, versionId }) {
    try {
      await request.post(`/incoming_tenders/offers/versions/${versionId}/submit/`);
      await dispatch('loadIncomingTenderVersions', { incomingTenderId });
      dispatch('loadIncomingTenderInfo', incomingTenderId);
    } catch (e) {
      console.error(e);
    }
  },
  async uploadXlsxTemplate(
    { dispatch, commit },
    { incomingTenderId, file, offerVersionId = null }
  ) {
    commit('changeExportXlsxLoading', true);
    commit('changeExportXlsxInfo', { errorText: null });
    try {
      let versionId = offerVersionId;
      // Если пришел запрос на выгрузку эксельки не в
      // рамках созданной драфтовой версии, то сначала создаем версию
      if (!versionId) {
        const { data } = await request.post('/incoming_tenders/offers/versions/', {
          tender_offer_id: incomingTenderId,
        });
        versionId = data.id;
      }
      let fd = new FormData();
      fd.set('file', file);
      await request.post(`/incoming_tenders/offers/versions/${versionId}/xlsx/`, fd);
    } catch (e) {
      if (e.response?.data?.detail) {
        commit('changeExportXlsxInfo', { errorText: e.response.data.detail });
      }
      console.error(e.response.data.detail); // eslint-disable-line
    }
    await dispatch('loadIncomingTenderVersions', { incomingTenderId });
    commit('changeExportXlsxLoading', false);
  },
  async createBreakdownRow({ commit, rootState }, { rowId, total }) {
    try {
      const meterUnit = rootState.global.units.find((elem) => elem.name === 'm');
      const { data } = await request.post(
        `/incoming_tenders/offers/rows/${rowId}/breakdown_rows/`,
        {
          tender_offer_row_id: rowId,
          unit_id: meterUnit.id,
          total: total,
        }
      );
      commit('addNewBreakdownRow', { rowId, createdBreakdownRow: data });
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
    commit('refreshIncomingTenderListsInfo');
    commit('incomingTableRefresher');
  },
  async deleteIncomingTenderBreakdownRow({ commit }, { rowId, breakdownRowId, breakdownRowIndex }) {
    try {
      await request.delete(
        `/incoming_tenders/offers/rows/${rowId}/breakdown_rows/${breakdownRowId}/`
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    commit('removeBreakdownRow', { rowId, breakdownRowIndex });
    commit('refreshIncomingTenderListsInfo');
    commit('incomingTableRefresher');
  },
  async deleteIncomingTenderBreakdown({ commit }, { rowId }) {
    try {
      await request.delete(`/incoming_tenders/offers/rows/${rowId}/breakdown_rows/all/`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    commit('removeBreakdown', { rowId });
    commit('refreshIncomingTenderListsInfo');
    commit('incomingTableRefresher');
  },
  switchChangesFromCustomer({ commit, state }) {
    commit('setChangesFromCustomer', !state.showChangesFromCustomer);
  },
  setChangesFromCustomer({ commit }, value) {
    commit('setChangesFromCustomer', value);
  },
  async archiveOfferVersion({ state, dispatch }, { offerVersionId, archive }) {
    try {
      await request.post(
        `/incoming_tenders/offers/versions/${offerVersionId}/${archive ? 'archive' : 'unarchive'}/`
      );
      await dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
      dispatch('loadIncomingTenderInfo', state.incomingTenderInfo.id);
    } catch (e) {
      console.error(e);
    }
  },
  async editVersionDescription({ state, dispatch }, { versionId, description }) {
    try {
      await factory('incoming_tenders/offers/versions').patchInPath(versionId, {
        description,
      });
      await dispatch('loadIncomingTenderVersions', {
        incomingTenderId: state.incomingTenderInfo.id,
      });
    } catch (e) {
      console.error(e);
    }
  },
};

export const getters = {
  versionCanBeAdded: (state) => {
    return !state.incomingTenderVersions.some(
      (version) => version.status === TENDER_OFFER_VERSION_STATUSSES.draft
    );
  },
  draftEmptyPositions: (state) => {
    const draftVersion = state.incomingTenderVersions.find(
      (version) => version.status === TENDER_OFFER_VERSION_STATUSSES.draft
    );
    return draftVersion?.empty_positions_count;
  },
  draftVersion: (state) => {
    return state.incomingTenderVersions.find(
      (version) => version.status === TENDER_OFFER_VERSION_STATUSSES.draft
    );
  },
  isAtLeastOneDraft: (state) => {
    return get(state, 'incomingTenderVersions', []).some(
      (version) => version.status === TENDER_OFFER_VERSION_STATUSSES.draft
    );
  },
  isNotInvitedIncomingTender: (state) => {
    const status = state.incomingTenderInfo?.status;
    return status === 'read' || status === 'invited' || status === 'declined';
  },
  isDeclinedIncomingTender: (state) => {
    const status = state.incomingTenderInfo?.status;
    return status === 'declined';
  },
  offerCanBeAdded: (state) => {
    let status = state.incomingTenderInfo?.status;
    return status === 'participating' || status === 'offered';
  },
  lastVersionIsDraft: (state) => {
    return (
      state.incomingTenderVersions?.length === 1 &&
      state.incomingTenderVersions[0]?.status === TENDER_OFFER_VERSION_STATUSSES.draft
    );
  },
  selectedVersion: (state) => {
    return state.incomingTenderVersions?.find((version) => version.id === state.selectedVersionId);
  },
};

export default { namespaced: true, state, mutations, actions, getters };
